import axiosInstance from 'utils/axiosInstance';
import { extractApiErrorMessage } from '../apiError';
import { FIRSTCHEKOUT_STATUS_PATH } from '../card/FirstCheckoutHandler';
import {
  ITransferHandler,
  TransferAccountDetails,
  TransferInitiationPayload,
} from '../types';

interface FirstChekoutTransferResponse {
  id?: string;
  transactionReference?: string;
  responseCode?: 'APPROVED' | 'PENDING' | 'DECLINED' | 'FAILED';
  message?: string;
  virtualAccountNumber?: string;
  virtualAccountName?: string;
  bankProviderCode?: string;
}

// FirstChekout's status values in the checkout's vocabulary. INITIATED and
// PENDING are absent on purpose: the customer may not have paid yet.
const STATUS_MAP: Record<string, string> = {
  SUCCESS: 'APPROVED',
  FAILED: 'FAILED',
  EXPIRED: 'TIMED_OUT',
};

const isRejected = (data: FirstChekoutTransferResponse) =>
  data.responseCode === 'DECLINED' || data.responseCode === 'FAILED';

const post = (path: string, body: object) =>
  axiosInstance
    .post<FirstChekoutTransferResponse>(path, body)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(extractApiErrorMessage(error));
    });

export class FirstCheckoutTransferHandler implements ITransferHandler {
  /**
   * FirstChekout takes the amount in Naira and answers PENDING with the
   * account to pay into. It sets no expiry.
   */
  async initiateTransfer(
    payload: TransferInitiationPayload,
  ): Promise<TransferAccountDetails> {
    const data = await post('/firstchekout/payment/transfer', {
      transactionId: payload.transactionId,
      amount: payload.amount / 100,
      email: payload.email,
      payerName: payload.payerName,
      purpose: payload.purpose,
    });

    if (
      isRejected(data) ||
      !data.virtualAccountNumber ||
      !data.transactionReference
    ) {
      throw new Error(
        data.message ||
        'FirstChekout could not generate an account to pay into.',
      );
    }

    return {
      virtualAccountNumber: data.virtualAccountNumber,
      virtualAccountName: data.virtualAccountName ?? '',
      bankCode: data.bankProviderCode || '',
      validityTime: undefined,
      reference: data.transactionReference,
    };
  }

  getStatusUrl(reference: string): string {
    return `${FIRSTCHEKOUT_STATUS_PATH}/${reference}`;
  }

  readStatus(data: any): string | undefined {
    const status =
      typeof data?.status === 'string' ? data.status.toUpperCase() : '';
    return STATUS_MAP[status];
  }

  /**
   * Resolves once FirstChekout accepts the confirmation. Settlement is still
   * read from the status endpoint.
   */
  async confirmTransfer(transactionId: string): Promise<void> {
    const data = await post('/firstchekout/payment/transfer/confirm', {
      transactionId,
    });

    if (isRejected(data)) {
      throw new Error(
        data.message || 'Your transfer has not been received yet.',
      );
    }
  }
}
